import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import Particles, { initParticlesEngine } from '@tsparticles/react';
import { loadSlim } from '@tsparticles/slim';
import type { IManualParticle, ISourceOptions } from '@tsparticles/engine';
import { theme } from '../../styles/theme';

/**
 * Malha de pontos conectados usada como fundo das seções de destaque.
 *
 * Substitui as duas configurações de partículas que existiam duplicadas
 * (HeroSection e ProductPage/ParticlesBackground), com ~150 linhas quase
 * idênticas cada e 60 pontos posicionados à mão.
 */

/**
 * A tela ocupa apenas a faixa direita da seção. Isso mantém a malha longe do
 * texto de forma estrutural: as partículas quicam nos limites da tela, então
 * com a tela do tamanho da seção inteira elas acabavam se espalhando por cima
 * do conteúdo depois de algum tempo. Também reduz pela metade a área a desenhar.
 */
const Layer = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 52%;
  /* A camada não captura o ponteiro: o tsparticles escuta os eventos na janela
     (detectsOn padrão), então a interação funciona sem roubar cliques do hero. */
  pointer-events: none;
  z-index: 1;

  /* O tsparticles dimensiona a tela pelo elemento que ele mesmo cria; sem uma
     altura explícita ele adota uma proporção própria e a malha ocupa só parte
     da faixa. */
  > div {
    width: 100%;
    height: 100%;
  }

  /* Esmaece a borda esquerda para a malha não terminar num corte reto. */
  -webkit-mask-image: linear-gradient(to right, transparent 0%, #000 24%);
  mask-image: linear-gradient(to right, transparent 0%, #000 24%);

  @media (max-width: ${theme.breakpoints.lg}) {
    width: 62%;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    -webkit-mask-image: linear-gradient(to bottom, #000 55%, transparent 100%);
    mask-image: linear-gradient(to bottom, #000 55%, transparent 100%);
  }
`;

/** Malha distribuída na tela inteira, adensando em direção à borda direita. */
const COLUMNS = 8;
const ROWS = 9;

/**
 * Ruído determinístico. Mantém a mesma composição entre renderizações — com
 * Math.random a malha se reorganizaria a cada re-render do componente.
 */
const noise = (index: number, salt: number) =>
  Math.abs(Math.sin(index * 12.9898 + salt * 78.233) * 43758.5453) % 1;

/**
 * Gera a malha concentrada à direita, para não competir com o texto do hero.
 * A densidade cresce em direção à borda direita, o que dá profundidade sem
 * precisar de pontos posicionados manualmente.
 */
const buildField = (palette: string[], compact: boolean): IManualParticle[] => {
  const field: IManualParticle[] = [];
  const stepX = 96 / (COLUMNS - 1);
  const stepY = 92 / (ROWS - 1);

  for (let row = 0; row < ROWS; row += 1) {
    for (let column = 0; column < COLUMNS; column += 1) {
      const index = row * COLUMNS + column;

      // Mais rarefeito à esquerda, onde a malha encosta no texto. Em telas
      // pequenas a malha inteira é cortada quase pela metade: o cálculo de
      // ligações cresce com o quadrado do número de pontos.
      const density = (0.35 + (column / (COLUMNS - 1)) * 0.65) * (compact ? 0.55 : 1);
      if (noise(index, 1) > density) continue;

      field.push({
        position: {
          mode: 'percent',
          x: 2 + column * stepX + (noise(index, 2) - 0.5) * stepX * 0.8,
          y: 4 + row * stepY + (noise(index, 3) - 0.5) * stepY * 0.8,
        },
        options: {
          color: { value: palette[index % palette.length] },
          size: { value: 2 + noise(index, 4) * 2.5 },
        },
      });
    }
  }

  return field;
};

interface NetworkBackgroundProps {
  /** Cor de acento da seção — normalmente a cor da solução. */
  accentColor?: string;
}

const NetworkBackground: React.FC<NetworkBackgroundProps> = ({
  accentColor = theme.colors.primary.synapse,
}) => {
  const [ready, setReady] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    let active = true;

    initParticlesEngine(async (engine) => {
      await loadSlim(engine);
    }).then(() => {
      if (active) setReady(true);
    });

    return () => {
      active = false;
    };
  }, []);

  // Respeita "reduzir movimento" do sistema: a malha continua visível, porém
  // parada, sem deslocamento, pulsação nem reação ao ponteiro.
  useEffect(() => {
    const queries: Array<[MediaQueryList, (matches: boolean) => void]> = [
      [window.matchMedia('(prefers-reduced-motion: reduce)'), setReducedMotion],
      [window.matchMedia(`(max-width: ${theme.breakpoints.sm})`), setCompact],
    ];

    const listeners = queries.map(([query, apply]) => {
      const sync = () => apply(query.matches);
      sync();
      query.addEventListener('change', sync);
      return () => query.removeEventListener('change', sync);
    });

    return () => listeners.forEach((remove) => remove());
  }, []);

  const options: ISourceOptions = useMemo(() => {
    // O acento aparece com o dobro da frequência das cores de apoio.
    const palette = [
      accentColor,
      theme.colors.primary.data,
      accentColor,
      theme.colors.primary.accent,
      accentColor,
      theme.colors.primary.synapse,
    ];

    return {
      fullScreen: { enable: false },
      background: { color: { value: 'transparent' } },
      detectRetina: true,
      // 60 quadros bastam para esta densidade; o valor anterior (120) dobrava
      // o trabalho sem diferença perceptível.
      fpsLimit: 60,
      // Não gasta bateria com a aba em segundo plano ou a seção fora da tela.
      pauseOnBlur: true,
      pauseOnOutsideViewport: true,

      interactivity: {
        events: {
          onHover: {
            enable: !reducedMotion,
            // "grab" liga o ponteiro aos pontos próximos, em vez de empurrá-los:
            // a interação passa a ilustrar a conexão de dados da marca.
            mode: 'grab',
            parallax: { enable: !reducedMotion, force: 18, smooth: 26 },
          },
          resize: { enable: true, delay: 0.5 },
        },
        modes: {
          grab: {
            distance: 140,
            links: { opacity: 0.6, color: accentColor },
          },
        },
      },

      particles: {
        color: { value: palette },
        links: {
          enable: true,
          color: accentColor,
          distance: 150,
          // Traço mais discreto que o cinza anterior: a malha fica coesa com a
          // marca e para de disputar atenção com o texto.
          opacity: 0.22,
          width: 1,
        },
        move: {
          enable: !reducedMotion,
          direction: 'none',
          outModes: { default: 'bounce' },
          random: false,
          speed: 0.6,
          straight: false,
        },
        number: {
          value: 0,
          density: { enable: false },
        },
        opacity: {
          value: { min: 0.35, max: 0.85 },
          animation: {
            enable: !reducedMotion,
            speed: { min: 0.1, max: 0.35 },
            startValue: 'random',
            sync: false,
          },
        },
        shape: { type: 'circle' },
        size: {
          value: { min: 2, max: 4.5 },
          animation: { enable: false },
        },
      },

      manualParticles: buildField(palette, compact),

      // Em telas pequenas a malha passa por trás do texto: traço mais fraco e
      // pontos menores preservam a leitura.
      responsive: [
        {
          maxWidth: 640,
          mode: 'screen',
          options: {
            particles: {
              links: { distance: 110, opacity: 0.14 },
              size: { value: { min: 1.5, max: 3 } },
            },
          },
        },
      ],
    };
  }, [accentColor, reducedMotion, compact]);

  if (!ready) return null;

  return (
    <Layer aria-hidden="true">
      <Particles id={`network-${accentColor.replace('#', '')}`} options={options} />
    </Layer>
  );
};

export default NetworkBackground;
